<!--
  The dashboard's filter bar: one picker per facet (Org, Repo, Author,
  Waiting on), the chosen values as chips that take them back, and Clear.
  It renders `filter` and `options` and reports every change (`onchange`);
  it keeps no filter of its own.

  A filter the page's link names, which is not the user's own, is said to
  be one (`shared`), with "Save as my filter" and "Back to my filter"; a
  change that could not be saved (`save` is `failed`) is said too, with
  Retry. Both show whether or not the bar is collapsed. `summary` (what
  the filter shows, in words) is read out politely as it changes.

  A picker is a button that opens a combobox over a multi-select listbox:
  typing narrows the options, the arrow keys move through them, Enter
  chooses or takes back the one the keys are on, Esc closes it and gives the
  focus back to its button. The list stays open while values are chosen.

  At phone width, and always when `compact` (the extension's popup), the
  bar is one "Filters (n)" button that shows and hides the pickers and
  chips, with Clear beside it while a filter is on. Wide, a picker opens as
  a popover; collapsed, in place under the pickers.
-->
<script lang="ts">
  import { tick } from 'svelte';
  import { activeCount, chipsOf, FACET_LABEL, FACETS, NO_FILTER, toggle, type DashFilter, type Facet, type FacetOption } from '../logic/dashFilter';
  import Popover from './Popover.svelte';

  interface Props {
    filter: DashFilter;
    /** What each picker offers (`facetOptions`). */
    options: Record<Facet, readonly FacetOption[]>;
    onchange: (filter: DashFilter) => void;
    /** Collapsed to its button at every width. */
    compact?: boolean;
    /** The filter shown is a link's, not the user's own: looked at, not saved. */
    shared?: boolean;
    /** Make the link's filter the user's own. */
    onsave?: () => void;
    /** Leave the link's filter for the user's own. */
    onback?: () => void;
    /** Whether the user's filter is saved to their account, on its way, or could not be. */
    save?: 'saved' | 'saving' | 'failed';
    /** Send again a change that could not be saved. */
    onretry?: () => void;
    /** What the filter shows, in words ("Showing 4 of 19 pull requests"): read out politely when it changes. */
    summary?: string;
  }
  let { filter, options, onchange, compact = false, shared = false, onsave, onback, save = 'saved', onretry, summary = '' }: Props = $props();

  const uid = $props.id();
  const count = $derived(activeCount(filter));
  const chips = $derived(chipsOf(filter, options));

  /** Whether the collapsed bar shows its pickers and chips. */
  let shown = $state(false);
  /** The facet whose picker is open. */
  let picking = $state<Facet | null>(null);
  let query = $state('');
  /** The option the arrow keys are on, by its place among those listed. */
  let at = $state(0);
  const buttons: Partial<Record<Facet, HTMLButtonElement>> = $state({});
  let toggleButton = $state<HTMLButtonElement | null>(null);
  let panel = $state<HTMLElement | null>(null);

  const listed = $derived.by(() => {
    if (!picking) return [];
    const q = query.trim().toLowerCase();
    return options[picking].filter((o) => !q || o.label.toLowerCase().includes(q));
  });
  const active = $derived(listed.length ? Math.min(at, listed.length - 1) : -1);
  const optionId = (i: number) => `${uid}-option-${i}`;

  /** Whether the bar is collapsed to its button: `compact`, or the stylesheet's phone width. */
  const collapsed = () => compact || (typeof matchMedia === 'function' && matchMedia('(max-width: 760px)').matches);

  function open(facet: Facet) {
    if (picking === facet) return close(false);
    picking = facet;
    query = '';
    at = 0;
    // In place, the panel is not a popover: it takes the focus itself.
    if (collapsed()) void tick().then(() => panel?.querySelector<HTMLElement>('input')?.focus());
  }

  function close(refocus: boolean) {
    // The focus moves first, while the picker still knows its button: its panel is then gone, not left.
    if (refocus && picking) buttons[picking]?.focus();
    picking = null;
  }

  function pick(o: FacetOption) {
    if (picking) onchange(toggle(filter, picking, o.value));
  }

  function listKey(e: KeyboardEvent) {
    const move = (to: number) => {
      e.preventDefault();
      if (!listed.length) return;
      at = (to + listed.length) % listed.length;
      void tick().then(() => document.getElementById(optionId(at))?.scrollIntoView({ block: 'nearest' }));
    };
    if (e.key === 'ArrowDown') move(active + 1);
    else if (e.key === 'ArrowUp') move(active - 1);
    else if (e.key === 'Home' && !query) move(0);
    else if (e.key === 'End' && !query) move(listed.length - 1);
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (active >= 0) pick(listed[active]);
    } else if (e.key === 'Escape') {
      // The popover closes itself; in place, this does.
      e.preventDefault();
      e.stopPropagation();
      close(true);
    }
  }

  /** The focus left the in-place panel for something other than its own button. */
  function panelFocusOut(e: FocusEvent) {
    const to = e.relatedTarget as Node | null;
    if (to && !panel?.contains(to) && !(picking && buttons[picking]?.contains(to))) close(false);
  }

  /** Put the focus in the bar: on its first picker, shown first when the bar is collapsed. */
  export async function focus() {
    if (collapsed()) shown = true;
    await tick();
    (buttons[FACETS[0]] ?? toggleButton)?.focus();
  }

  function clear() {
    picking = null;
    onchange(NO_FILTER);
    // Its chips, and Clear with them, are gone: the focus stays in the bar.
    void tick().then(() => (collapsed() && !shown ? toggleButton : buttons[FACETS[0]])?.focus());
  }

  function remove(facet: Facet, value: string, i: number) {
    onchange(toggle(filter, facet, value));
    // The focus goes to the chip that takes this one's place, or to the pickers.
    void tick().then(() => {
      const left = [...document.getElementById(`${uid}-chips`)?.querySelectorAll<HTMLElement>('.filter-chip') ?? []];
      (left[Math.min(i, left.length - 1)] ?? buttons[facet])?.focus();
    });
  }
</script>

{#snippet picker(facet: Facet)}
  <input
    class="facet-search"
    type="text"
    role="combobox"
    aria-label="Find in {FACET_LABEL[facet]}"
    aria-expanded="true"
    aria-controls="{uid}-list"
    aria-autocomplete="list"
    aria-activedescendant={active >= 0 ? optionId(active) : undefined}
    placeholder="Find…"
    autocomplete="off"
    spellcheck="false"
    data-autofocus
    bind:value={query}
    oninput={() => (at = 0)}
    onkeydown={listKey}
  />
  <ul class="facet-list" role="listbox" id="{uid}-list" aria-label={FACET_LABEL[facet]} aria-multiselectable="true">
    {#each listed as o, i (o.value)}
      <!-- The keys work the list from the combobox (aria-activedescendant); a click is the pointer's way. -->
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <li
        class="facet-option"
        class:active={i === active}
        role="option"
        id={optionId(i)}
        tabindex="-1"
        aria-selected={o.selected}
        onpointerdown={(e) => e.preventDefault()}
        onpointermove={() => (at = i)}
        onclick={() => pick(o)}
      >
        <span class="facet-check" aria-hidden="true">{o.selected ? '✓' : ''}</span>
        <span class="facet-name">{o.label}</span>
        <!-- The number is for the eye; the words after it are the option's name for a screen reader. -->
        <span class="facet-count" aria-hidden="true">{o.count}</span>
        <span class="sr-only">, {o.count} pull request{o.count === 1 ? '' : 's'}</span>
      </li>
    {:else}
      <li class="facet-none" role="presentation">{options[facet].length ? 'Nothing matches.' : 'Nothing loaded to choose from.'}</li>
    {/each}
  </ul>
{/snippet}

<div class="filters" class:compact class:shown role="group" aria-label="Filter pull requests">
  <div class="filters-collapsed">
    <button class="btn small" bind:this={toggleButton} aria-expanded={shown} aria-controls="{uid}-body" onclick={() => ((shown = !shown), (picking = null))}
      >Filters{count ? ` (${count})` : ''}</button
    >
    {#if count && !shown}<button class="filter-clear" aria-label="Clear all filters" onclick={clear}>Clear</button>{/if}
  </div>
  {#if shared}
    <p class="filter-note filter-shared" role="status">
      <span>Viewing a shared filter: it came with this link, and isn't saved as yours.</span>
      <button class="btn small" onclick={() => onsave?.()}>Save as my filter</button>
      <button class="btn small" onclick={() => onback?.()}>Back to my filter</button>
    </p>
  {/if}
  {#if save === 'failed'}
    <p class="filter-note filter-unsaved" role="alert">
      <span>Your filter couldn't be saved to your account, so your other browsers and the extension still have the earlier one.</span>
      <button class="btn small" onclick={() => onretry?.()}>Retry</button>
    </p>
  {/if}
  <p class="sr-only" role="status" aria-live="polite">{summary}</p>
  <div class="filters-body" id="{uid}-body">
    <div class="filters-pickers">
      <span class="eyebrow filters-label" aria-hidden="true">Filter</span>
      {#each FACETS as facet (facet)}
        {@const n = filter[facet].length}
        <button
          class="btn small facet"
          class:on={n > 0}
          bind:this={buttons[facet]}
          aria-haspopup="listbox"
          aria-expanded={picking === facet}
          aria-controls={picking === facet ? `${uid}-list` : undefined}
          aria-label="{FACET_LABEL[facet]}{n ? `, ${n} chosen` : ''}"
          onclick={() => open(facet)}
          >{FACET_LABEL[facet]}{#if n}<span class="facet-n" aria-hidden="true">{n}</span>{/if}<span class="facet-caret" aria-hidden="true"></span></button
        >
      {/each}
    </div>
    {#if picking && collapsed()}
      <div class="facet-panel" bind:this={panel} role="group" aria-label="Filter by {FACET_LABEL[picking]}" onfocusout={panelFocusOut}>
        {@render picker(picking)}
      </div>
    {/if}
    {#if chips.length}
      <ul class="filter-chips" id="{uid}-chips" role="list" aria-label="Active filters">
        {#each chips as c, i (c.facet + ':' + c.value)}
          <li>
            <button class="filter-chip" title="Remove this filter" aria-label="Remove filter {c.text}" onclick={() => remove(c.facet, c.value, i)}
              ><span class="filter-chip-facet">{FACET_LABEL[c.facet]}:</span> {c.label}<span class="filter-chip-x" aria-hidden="true">×</span></button
            >
          </li>
        {/each}
        <li><button class="filter-clear" aria-label="Clear all filters" onclick={clear}>Clear</button></li>
      </ul>
    {/if}
  </div>
</div>

{#if picking && !collapsed() && buttons[picking]}
  {@const facet = picking}
  <!-- A group, not a dialog: what opens is the listbox its button promises (aria-haspopup), with the combobox that works it. -->
  <Popover anchor={buttons[facet]!} role="group" label="Filter by {FACET_LABEL[facet]}" onclose={close}>
    <div class="facet-pop">{@render picker(facet)}</div>
  </Popover>
{/if}

<style>
  .filters {
    margin: 0 0 18px;
    font-size: 12px;
  }
  .filters-collapsed {
    display: none;
    align-items: center;
    gap: 12px;
  }
  .filters-body {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 16px;
  }
  .filters-pickers {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
  }
  .filters-label {
    margin-right: 4px;
  }
  .facet.on,
  .facet[aria-expanded='true'] {
    border-color: var(--accent);
  }
  .facet-n {
    min-width: 17px;
    padding: 0 5px;
    border-radius: 9px;
    background: var(--accent);
    color: var(--surface);
    font: 600 11px/16px ui-monospace, SFMono-Regular, Consolas, monospace;
    text-align: center;
  }
  .facet-caret {
    width: 5px;
    height: 5px;
    margin: -3px 1px 0 2px;
    border: solid var(--muted);
    border-width: 0 1.5px 1.5px 0;
    transform: rotate(45deg);
  }
  .filter-chips {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .filter-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 100%;
    border: 1px solid transparent;
    border-radius: 30px;
    padding: 3px 8px 3px 10px;
    background: var(--soft);
    color: var(--fg);
    font-size: 12px;
    line-height: 1.4;
    overflow-wrap: anywhere;
    text-align: left;
  }
  .filter-chip:hover {
    border-color: var(--accent);
  }
  .filter-chip-facet {
    color: var(--muted);
  }
  .filter-chip-x {
    margin-left: 2px;
    color: var(--muted);
    font-size: 14px;
    line-height: 1;
  }
  .filter-chip:hover .filter-chip-x {
    color: var(--accent);
  }
  .filter-clear {
    border: 0;
    background: none;
    padding: 3px 4px;
    color: var(--accent);
    font-size: 12px;
    text-decoration: underline;
    text-underline-offset: 3px;
  }
  .filter-note {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 10px;
    margin: 0 0 8px;
    padding: 6px 10px;
    border-radius: 8px;
    background: var(--warnbg);
    color: var(--warn);
    font-size: 12px;
  }
  .filter-unsaved {
    background: var(--badbg);
    color: var(--bad);
  }
  .filters-collapsed + .filter-note {
    margin-top: 8px;
  }
  .facet-pop {
    width: 260px;
    max-width: 100%;
  }
  .facet-panel {
    flex-basis: 100%;
    border: 1px solid var(--line);
    border-radius: 10px;
    padding: 6px;
    background: var(--surface);
  }
  .facet-search {
    width: 100%;
    padding: 6px 8px;
    font-size: 13px;
  }
  .facet-list {
    max-height: 264px;
    overflow: auto;
    margin: 6px 0 0;
    padding: 0;
    list-style: none;
  }
  .facet-option {
    display: grid;
    grid-template-columns: 14px minmax(0, 1fr) auto;
    align-items: baseline;
    gap: 6px;
    padding: 5px 8px;
    border-radius: 6px;
    font-size: 13px;
    cursor: pointer;
  }
  .facet-option.active {
    background: var(--soft);
  }
  .facet-option[aria-selected='true'] .facet-name {
    font-weight: 600;
  }
  .facet-check {
    color: var(--accent);
    font-size: 12px;
  }
  .facet-name {
    overflow-wrap: anywhere;
  }
  .facet-count {
    color: var(--muted);
    font: 11px ui-monospace, SFMono-Regular, Consolas, monospace;
  }
  .facet-none {
    padding: 6px 8px;
    color: var(--muted);
  }

  /* Collapsed to one button: always when compact, and at phone width. */
  .filters.compact .filters-collapsed {
    display: flex;
  }
  .filters.compact .filters-body {
    display: none;
  }
  .filters.compact.shown .filters-body {
    display: flex;
    margin-top: 8px;
  }
  .filters.compact .filters-label {
    display: none;
  }
  @media (max-width: 760px) {
    .filters .filters-collapsed {
      display: flex;
    }
    .filters .filters-body {
      display: none;
    }
    .filters.shown .filters-body {
      display: flex;
      margin-top: 8px;
    }
    .filters .filters-label {
      display: none;
    }
    /* Fingers: nothing smaller than GitHub's own controls. */
    .facet-option {
      padding: 9px 8px;
    }
  }
</style>
